import { useState, useEffect, useMemo, useContext } from "react";
import { usePostHog } from "posthog-js/react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import CustomText from "../components/common/general/CustomText/CustomText";
import { useGetNotEnrolledCoursesBySchoolQuery, useMeQuery } from "../graphql/generated/graphql";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import Loader from "../components/common/status/Loader/Loader";
import ErrorLoader from "../components/common/status/ErrorLoader/ErrorLoader";
import { MotiView } from "moti";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { useAppConfig } from "../AppConfigProvider";
import TabNavigator from "../components/customCoursework/CourseCatalogue/TabNavigator/TabNavigator";
import NoChatBarLayout from "../layouts/NoChatBarLayout.web";
import { CourseDetailsContext } from "../contexts/CourseDetailsContext";
import DropDownPicker from "react-native-dropdown-picker";
import CatalogueCard from "../components/customCoursework/CourseCatalogue/CatalogueCard/CatalogueCard";
import usePagination from "../hooks/usePagination";

const tabs = [
  {
    key: "all",
    title: "All",
  },
  {
    key: "my_degree_courses",
    title: "My Degree Courses",
  },
  {
    key: "public_courses",
    title: "Public Courses",
  },
];

const dropdownItems = [
  { label: "Price (Low to High)", value: "price-asc" },
  { label: "Price (High to Low)", value: "price-desc" },
  { label: "Name (A → Z)", value: "name-asc" },
  { label: "Name (Z → A)", value: "name-desc" },
];

const ITEMS_PER_PAGE = 50;

function CourseCatalogueView() {
  const [activeTab, setActiveTab] = useState("all");
  const [sortBy, setSortBy] = useState("price-asc");
  const [selectedDegree, setSelectedDegree] = useState("all");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [dropdownMenuVisible, setDropdownMenuVisible] = useState(false);
  const [degreeDropdownVisible, setDegreeDropdownVisible] = useState(false);
  const [degreeItems, setDegreeItems] = useState([]);
  const { setIsVisible, setCourseData, onEnroll, loading, courseData } = useContext(CourseDetailsContext);

  const navigation = useNavigation();

  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;
  const integrationId = appConfig.axioSchools[0];
  const hasDegrees = appConfig.hasDegrees;
  const { isMedium, isLarge } = useResponsiveScreen();

  const { data: meData } = useMeQuery();

  const [totalItemsCount, setTotalItemsCount] = useState(0);

  const { page, setPage, pageSize, PaginationComponent } = usePagination({
    totalItemsCount,
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const {
    data: courses,
    loading: coursesLoading,
    error: coursesError,
  } = useGetNotEnrolledCoursesBySchoolQuery({
    variables: {
      schoolId: integrationId,
      showCoursesWithDegrees: hasDegrees && activeTab === "my_degree_courses",
      showCoursesWithoutDegrees: hasDegrees && activeTab === "public_courses",
      page,
      pageSize,
    },
    skip: !integrationId,
  });

  useEffect(() => {
    setTotalItemsCount(courses?.getNotEnrolledCoursesBySchool?.data?.totalCount || 0);
  }, [courses]);

  useEffect(() => {
    if (courses?.getNotEnrolledCoursesBySchool?.data?.courses) {
      const uniqueDegrees = new Set();
      courses.getNotEnrolledCoursesBySchool.data.courses.forEach((course) => {
        if (course.degreesHasCourse && course.degreesHasCourse.length > 0) {
          course.degreesHasCourse.forEach((degree) => {
            if (degree.usersEnrolledIn?.some((user) => user?.id === meData?.me?.id)) {
              uniqueDegrees.add(JSON.stringify({ label: degree.name, value: degree.id }));
            }
          });
        }
      });

      const degreeDropdownItems = [{ label: "All My Degrees", value: "all" }];
      uniqueDegrees.forEach((degree) => {
        degreeDropdownItems.push(JSON.parse(degree));
      });

      setDegreeItems(degreeDropdownItems);
    }
  }, [courses, meData?.me?.id]);

  const posthog = usePostHog();

  useEffect(() => {
    posthog?.capture("Course Catalogue Page Visited");
  }, []);

  const handleDetailsPress = (course) => {
    setCourseData({
      id: course?.id,
      name: course?.name,
      description: course?.description,
      objectives: course?.objectives?.[0],
      targetAudience: course?.targetAudience,
      cost: course?.cost,
      modules: course?.modules?.map((module) => ({
        name: module.title,
        activities: [
          ...(module?.assignments?.map((assignment) => ({
            name: assignment.name,
            id: assignment.id,
          })) || []),
          ...(module?.aiTutors?.map((aiTutor) => ({
            name: aiTutor.title,
            id: aiTutor.id,
          })) || []),
          ...(module?.axioResources?.map((resource) => ({
            name: resource.name,
            id: resource.id,
          })) || []),
        ],
      })),
    });
    setIsVisible(true);
  };

  const handleEnrollPress = (courseId, hasCost) => {
    setSelectedCourse(courseId);

    onEnroll(courseId, hasCost, () => {
      navigation.navigate("Custom School Student", {
        integration_id: integrationId,
      });
    });
  };

  const courseTypeMap = useMemo(() => {
    const map = new Map();
    const allCourses = courses?.getNotEnrolledCoursesBySchool?.data?.courses || [];
    map.set("all", allCourses);
    map.set(
      "my_degree_courses",
      allCourses.filter((course) => course.degreesHasCourse && course.degreesHasCourse.length > 0)
    );
    map.set(
      "public_courses",
      allCourses.filter((course) => !course.degreesHasCourse || course.degreesHasCourse.length === 0)
    );

    return map;
  }, [courses?.getNotEnrolledCoursesBySchool?.data?.courses]);

  const filteredData = courseTypeMap.get(activeTab) || [];

  const filteredByDegree = useMemo(() => {
    if (selectedDegree === "all") {
      return filteredData;
    }

    return filteredData.filter(
      (course) => course.degreesHasCourse && course.degreesHasCourse.some((degree) => degree.id === selectedDegree)
    );
  }, [filteredData, selectedDegree]);

  const sortedData = useMemo(() => {
    return [...filteredByDegree].sort((a, b) => {
      switch (sortBy) {
        case "price-asc":
          return a.cost - b.cost;
        case "price-desc":
          return b.cost - a.cost;
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });
  }, [filteredByDegree, sortBy]);

  if (coursesLoading) {
    return (
      <NoChatBarLayout>
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      </NoChatBarLayout>
    );
  }
  if (coursesError) {
    return (
      <NoChatBarLayout>
        <View style={styles.loaderContainer}>
          <ErrorLoader message="Something went wrong! Try reloading the page." />
        </View>
      </NoChatBarLayout>
    );
  }

  return (
    <NoChatBarLayout>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={10}
        transition={{ type: "timing", duration: 500 }}>
        <View style={{ flexDirection: "column", justifyContent: "flex-start" }}>
          <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
            <CustomText
              text={"Course Catalogue"}
              size={isMedium ? "m" : "xs"}
              textType="display"
              weight="ultraBold"
              role="heading"
              aria-level="1"
              style={{ letterSpacing: -0.72, color: "#101828" }}
            />
          </View>
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            text="Discover courses tailored to your goals. Enroll in the classes that help you level up your skills."
            size="m"
            weight="normal"
            style={{ color: "#667085" }}
          />
        </View>
      </MotiView>
      <CustomSpacing type="vertical" size="xxl" />
      <CustomSpacing type="vertical" size="xs" />
      <View style={isLarge ? styles.filteringContainerMedium : styles.filteringContainer}>
        {hasDegrees ? (
          <TabNavigator
            tabData={tabs}
            activeTab={activeTab}
            onTabPress={(key) => {
              setActiveTab(key);
            }}
          />
        ) : (
          <View />
        )}
        <View style={styles.filtersContainer}>
          {degreeItems.length > 2 && activeTab === "my_degree_courses" && (
            <View style={styles.sortByContainer}>
              <CustomText
                numberOfLines={1}
                ellipsizeMode="tail"
                size="s"
                weight="normal"
                style={{ color: "#667085", minWidth: 85 }}
                text="My Degrees"
              />
              <DropDownPicker
                open={degreeDropdownVisible}
                value={selectedDegree}
                items={degreeItems}
                setOpen={setDegreeDropdownVisible}
                setValue={setSelectedDegree}
                dropDownContainerStyle={styles.dropdownContainer}
                style={styles.dropdownField}
                textStyle={styles.dropdownText}
                zIndex={3000}
              />
            </View>
          )}
          <View style={styles.sortByContainer}>
            <CustomText
              numberOfLines={1}
              ellipsizeMode="tail"
              size="s"
              weight="normal"
              style={{ color: "#667085", minWidth: 50 }}
              text="Sort by"
            />
            <DropDownPicker
              open={dropdownMenuVisible}
              value={sortBy}
              items={dropdownItems}
              setOpen={setDropdownMenuVisible}
              setValue={setSortBy}
              dropDownContainerStyle={styles.dropdownContainer}
              style={styles.dropdownField}
              textStyle={styles.dropdownText}
              zIndex={2000}
            />
          </View>
        </View>
      </View>

      <CustomSpacing type="vertical" size="xl" />
      <View style={styles.divider} />
      <CustomSpacing type="vertical" size="xxl" />
      <CustomSpacing type="vertical" size="xs" />

      {totalItemsCount > ITEMS_PER_PAGE && PaginationComponent}
      <CustomSpacing type="vertical" size="m" />

      <View role="grid" aria-label="Course cards" style={isMedium ? styles.gridContainer : styles.flexContainer}>
        {sortedData.map((course, index) => {
          const userHasDegree = course?.degreesHasCourse?.some((degree) =>
            degree?.usersEnrolledIn?.some((user) => user?.id === meData?.me?.id)
          );

          return (
            <CatalogueCard
              key={index}
              degreeNames={course?.degreesHasCourse?.map((degree) => degree.name)}
              userHasDegree={userHasDegree}
              title={course?.name}
              cost={course?.cost}
              description={course?.description}
              imageUrl={course?.thumbnail}
              onEnrollPress={() => handleEnrollPress(course?.id, !!course?.cost)}
              onDetailsPress={() =>
                handleDetailsPress({
                  ...course,
                })
              }
              isLoading={selectedCourse === course?.id && loading}
            />
          );
        })}
      </View>

      <CustomSpacing type="vertical" size="m" />
      {totalItemsCount > ITEMS_PER_PAGE && PaginationComponent}
    </NoChatBarLayout>
  );
}

const styles = StyleSheet.create({
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
    rowGap: 28,
    columnGap: 16,
  },
  flexContainer: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    rowGap: 28,
    columnGap: 16,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  divider: {
    height: 1,
    backgroundColor: "#EAECF0",
  },
  filteringContainerMedium: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    zIndex: 4000,
    rowGap: 0,
  },
  filteringContainer: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    zIndex: 4000,
    rowGap: 12,
  },
  filtersContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    columnGap: 16,
    zIndex: 3000,
  },
  sortByContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    columnGap: 8,
    zIndex: 2000,
  },
  dropdownField: {
    width: 164,
    borderColor: "#EAECF0",
    zIndex: 1000,
    elevation: 1000,
    height: 40,
  },
  dropdownContainer: {
    width: 164,
    borderColor: "#EAECF0",
    backgroundColor: "white",
  },
  dropdownText: {
    fontFamily: "Inter",
    fontSize: 12,
    fontWeight: "700",
    color: "#344054",
  },
});

export default CourseCatalogueView;
